import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
    ArrowRight, Clock, Truck, Shield, MapPin, Phone, Mail, 
    Instagram, Facebook, Twitter, Youtube, Send, Utensils, 
    Star, CheckCircle2, Heart, MessageSquare, ExternalLink 
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { useToast } from '../context/ToastContext';
import { cleanLogoUrl } from '../utils/logo';
import TableBookingWidget from '../components/storefront/TableBookingWidget';
import { Calendar } from 'lucide-react';
import api from '../services/api';
export default function Home() {
    const { user } = useAuth();
    const { tenant, isFranchise, franchise, branches } = useTenant();
    const navigate = useNavigate();
    const toast = useToast();

    const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', message: '' });
    const [submitting, setSubmitting] = useState(false);
    const [galleryCategory, setGalleryCategory] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [showBookingModal, setShowBookingModal] = useState(false);

    const rawHs = isFranchise ? franchise?.home_settings : tenant?.home_settings;
    const hs = typeof rawHs === 'string' ? JSON.parse(rawHs) : rawHs || {};

    const defaultGalleryItems = [
        { id: 1, title: 'Luxury Main Dining Hall', category: 'ambiance', image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=800&q=80', tag: 'Restaurant Interior' },
        { id: 2, title: 'Romantic Outdoor Garden Patio', category: 'ambiance', image: 'https://images.unsplash.com/photo-1543007630-9710e4a00a20?auto=format&fit=crop&w=800&q=80', tag: 'Outdoor Dining' },
        { id: 3, title: 'Executive Cocktail Lounge & Bar', category: 'ambiance', image: 'https://images.unsplash.com/photo-1572116469696-31de0f17cc34?auto=format&fit=crop&w=800&q=80', tag: 'Lounge Bar' },
        { id: 4, title: 'Signature Artisan Pizza', category: 'mains', image: 'https://images.unsplash.com/photo-1513104890138-7c749659a591?auto=format&fit=crop&w=800&q=80', tag: 'Chef Choice' },
        { id: 5, title: 'Gourmet Smashed Burger', category: 'mains', image: 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?auto=format&fit=crop&w=800&q=80', tag: 'Best Seller' },
        { id: 6, title: 'Decadent Chocolate Lava Cake', category: 'desserts', image: 'https://images.unsplash.com/photo-1606313564200-e75d5e30476c?auto=format&fit=crop&w=800&q=80', tag: 'Sweet Delight' }
    ];

    const galleryItems = Array.isArray(hs.galleryItems) && hs.galleryItems.length > 0 ? hs.galleryItems : defaultGalleryItems;

    const filteredGallery = galleryCategory === 'all' 
        ? galleryItems 
        : galleryItems.filter(item => item.category === galleryCategory);

    const operatingHours = Array.isArray(hs.operatingHours) && hs.operatingHours.length > 0 ? hs.operatingHours : [
        { day: 'Monday - Thursday', time: hs.monThuHours || '11:00 AM - 10:30 PM', status: 'Open' },
        { day: 'Friday - Saturday', time: hs.friSatHours || '11:00 AM - 11:30 PM', status: 'Open Late' },
        { day: 'Sunday', time: hs.sunHours || '12:00 PM - 10:00 PM', status: 'Open' }
    ];

    const restaurantName = tenant?.name || 'Savory Bites Restaurant';
    const restaurantAddress = hs.locationAddress || tenant?.address || '123 Gourmet Boulevard, Foodie Haven, Chennai, Tamil Nadu';
    const mapEmbedUrl = hs.mapEmbedUrl || 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3886.883737482869!2d80.2472!3d13.0475!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTPCsDAyJzUxLjAiTiA4MMKwMTQnNDkuOSJF!5e0!3m2!1sen!2sin!4v1650000000000!5m2!1sen!2sin';
    const restaurantPhone = hs.contactPhone || tenant?.contact_phone || '+91 98765 43210';
    const restaurantEmail = hs.contactEmail || tenant?.contact_email || 'contact@savorybites.com';
    const heroBgImage = hs.heroImage || 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1920&q=80';
    const heroSubtitleText = hs.heroSubtitle || 'Exquisite flavours, handcrafted recipes, and lightning-fast delivery straight to your doorstep.';

    const heroTitleStyle = hs.heroTextColor ? { color: hs.heroTextColor } : {};
    const heroSubtitleStyle = hs.heroSubtitleColor ? { color: hs.heroSubtitleColor } : {};
    const headingTextStyle = hs.headingTextColor ? { color: hs.headingTextColor } : {};
    const bodyTextStyle = hs.bodyTextColor ? { color: hs.bodyTextColor } : {};
    const locationBgStyle = (isFranchise ? franchise?.secondary_color : tenant?.secondary_color) ? { backgroundColor: isFranchise ? franchise.secondary_color : tenant.secondary_color } : {};

    useEffect(() => {
        if (user) {
            const isBranchPreview = window.location.pathname.startsWith('/branch/');
            if (user.role === 'admin' || user.role === 'restaurant_admin') navigate('/admin');
            else if (user.role === 'franchise_admin' && !isBranchPreview) navigate('/admin');
            else if (user.role === 'kitchen_staff') navigate('/kitchen');
            else if (user.role === 'delivery_partner') navigate('/delivery');
        }
    }, [user, navigate]);

    // Filter branches based on search input
    const filteredBranches = branches.filter(branch => 
        branch.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
        (branch.city && branch.city.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (branch.address && branch.address.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    if (isFranchise && franchise) {
        return (
            <div className="bg-slate-50 text-slate-800 font-sans min-h-screen">
                {/* Hero section */}
                <div className="relative bg-gradient-to-r from-primary-700 via-primary-600 to-indigo-700 text-white overflow-hidden py-24 sm:py-32">
                    <div className="absolute inset-0 bg-cover bg-center opacity-15 mix-blend-overlay" style={{ backgroundImage: `url('${heroBgImage}')` }} />
                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center flex flex-col items-center">
                        {franchise.logo_url && (
                            <img src={cleanLogoUrl(franchise.logo_url)} alt={franchise.name} className="h-20 mb-6 object-contain rounded-2xl bg-white/10 p-2 backdrop-blur-sm shadow-xl" />
                        )}
                        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/20 backdrop-blur-md text-white text-xs font-bold uppercase tracking-widest mb-6 border border-white/30 shadow-lg animate-bounce-subtle">
                            <Utensils className="w-3.5 h-3.5" /> <span style={{ WebkitTextStroke: 'var(--text-stroke-hero, none)' }}>Premium Culinary</span>
                        </span>
                        <h1 className="text-5xl sm:text-7xl font-black tracking-tight mb-6" style={heroTitleStyle}>
                            {hs.heroTitle ? hs.heroTitle : <>Welcome to <span className="text-amber-300 underline underline-offset-8 decoration-amber-400/50">{franchise.name}</span></>}
                        </h1>
                        <p className="text-lg sm:text-2xl mb-8 max-w-2xl text-primary-100 font-medium" style={heroSubtitleStyle}>
                            {hs.heroSubtitle ? hs.heroSubtitle : 'Choose your nearest branch location below to view the exclusive menu and place your order.'}
                        </p>

                        {/* Search Bar */}
                        <div className="w-full max-w-md bg-white p-2 rounded-2xl shadow-xl flex items-center border border-slate-100 mt-4">
                            <input 
                                type="text"
                                placeholder="Search by branch name or city..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full px-4 py-3 outline-none text-slate-800 text-sm font-medium rounded-xl"
                            />
                        </div>
                    </div>
                </div>

                {/* Branches List */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-12">
                        <div>
                            <h2 className="text-3xl font-black text-slate-900">Our Branch Locations</h2>
                            <p className="text-slate-500 text-sm mt-1">Select a branch to browse their fresh menu and pricing.</p>
                        </div>
                        <span className="bg-slate-200 text-slate-800 text-xs font-bold px-3 py-1.5 rounded-full self-start sm:self-auto font-mono">
                            {filteredBranches.length} branch{filteredBranches.length !== 1 ? 'es' : ''} found
                        </span>
                    </div>
                    
                    {filteredBranches.length === 0 ? (
                        <div className="bg-white p-12 rounded-3xl border border-slate-100 shadow-sm text-center max-w-md mx-auto">
                            <Utensils className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                            <h3 className="text-lg font-bold text-slate-800">No Branches Match Your Search</h3>
                            <p className="text-slate-400 text-sm mt-1">Try entering another keyword or clearing the filter.</p>
                        </div>
                    ) : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                            {filteredBranches.map(branch => (
                                <div key={branch.id} className="bg-white rounded-3xl p-5 border border-slate-100 shadow-md hover:shadow-xl transition-all duration-300 flex flex-col justify-between group hover:border-primary-100">
                                    <div className="space-y-4">
                                        {/* Clickable Image Button */}
                                        <div 
                                            onClick={() => navigate(`/branch/${branch.slug}`)}
                                            className="relative aspect-[16/10] rounded-2xl overflow-hidden cursor-pointer shadow-inner bg-slate-100"
                                        >
                                            <img 
                                                src={branch.image_url || 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?auto=format&fit=crop&w=600&q=80'} 
                                                alt={branch.name} 
                                                className="w-full h-full object-cover group-hover:scale-105 transition-all duration-500"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <h3 className="font-black text-slate-900 text-lg group-hover:text-primary-600 transition-colors">{branch.name}</h3>
                                            <p className="text-slate-400 text-xs flex items-center gap-1 font-medium"><MapPin className="w-3.5 h-3.5 text-primary-500 flex-shrink-0" /> {branch.address}</p>
                                        </div>
                                    </div>
                                    <div className="pt-5 border-t border-slate-50 mt-5 flex justify-between items-center">
                                        <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 bg-slate-100 px-3 py-1 rounded-full">{branch.city || 'Chennai'}</span>
                                        <button 
                                            onClick={() => navigate(`/branch/${branch.slug}`)}
                                            className="px-4 py-2 bg-slate-950 text-white text-xs font-black uppercase tracking-widest rounded-xl hover:bg-primary-600 transition-colors shadow-md shadow-slate-950/10 flex items-center gap-1"
                                        >
                                            <span>Order Now</span>
                                            <ArrowRight className="w-3 h-3" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    const handleContactSubmit = async (e) => {
        e.preventDefault();
        try {
            setSubmitting(true);
            const payload = {
                restaurantId: tenant?.id,
                name: contactForm.name,
                email: contactForm.email,
                phone: contactForm.phone,
                message: contactForm.message
            };
            await api.post('/api/restaurants/public/contact', payload);
            toast.success('Thank you for reaching out! We have received your message and will get back to you shortly.');
            setContactForm({ name: '', email: '', phone: '', message: '' });
            setSubmitting(false);
        } catch (error) {
            console.error('Failed to submit message:', error);
            toast.error(error.response?.data?.error || 'Failed to send message. Please try again.');
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-slate-50 text-slate-800 font-sans">
            {/* 1. HERO SECTION */}
            <div className="relative bg-gradient-to-r from-primary-700 via-primary-600 to-indigo-700 text-white overflow-hidden">
                <div className="absolute inset-0 bg-cover bg-center opacity-15 mix-blend-overlay" style={{ backgroundImage: `url('${heroBgImage}')` }} />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32 flex flex-col items-center text-center">
                    <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/20 backdrop-blur-md text-white text-xs font-bold uppercase tracking-widest mb-6 border border-white/30 shadow-lg">
                        <Utensils className="w-3.5 h-3.5" style={{ color: tenant?.primary_color || '#fbbf24' }} /> <span style={{ WebkitTextStroke: 'var(--text-stroke-hero, none)' }}>Premium Culinary</span>
                    </span>
                    <h1 className="text-4xl sm:text-6xl md:text-7xl font-black tracking-tight mb-6 leading-tight max-w-4xl" style={heroTitleStyle}>
                        {hs.heroTitle ? hs.heroTitle : <>Welcome to <span className="text-amber-300 underline underline-offset-8 decoration-amber-400/50">{restaurantName}</span></>}
                    </h1>
                    <p className="text-lg sm:text-2xl mb-10 max-w-2xl font-medium leading-relaxed text-primary-100" style={heroSubtitleStyle}>
                        {heroSubtitleText}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                        <Link
                            to="/menu"
                            className="inline-flex items-center justify-center space-x-3 bg-amber-400 text-slate-900 px-8 py-4 rounded-2xl font-black text-lg hover:bg-amber-300 transition-all shadow-xl hover:shadow-amber-400/30 transform hover:-translate-y-0.5"
                        >
                            <span>Explore Full Menu</span>
                            <ArrowRight className="w-5 h-5" />
                        </Link>
                        {tenant?.reservations_enabled && (
                            <button
                                onClick={() => setShowBookingModal(true)}
                                className="inline-flex items-center justify-center space-x-2 bg-white text-slate-900 px-8 py-4 rounded-2xl font-black text-lg hover:bg-slate-100 transition-all shadow-xl transform hover:-translate-y-0.5"
                            >
                                <Calendar className="w-5 h-5 text-primary-600" />
                                <span>Book a Table</span>
                            </button>
                        )}
                        <a
                            href="#location-hours"
                            className="inline-flex items-center justify-center space-x-2 bg-white/10 backdrop-blur-md text-white px-8 py-4 rounded-2xl font-bold text-lg hover:bg-white/20 transition-all border border-white/20"
                        >
                            <Clock className="w-5 h-5 text-amber-300" />
                            <span>Hours & Location</span>
                        </a>
                    </div>
                </div>
            </div>

            {/* FEATURES HIGHLIGHT */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 relative z-10">
                <div className="grid md:grid-cols-3 gap-6">
                    <div className="bg-white p-8 rounded-3xl shadow-xl border border-slate-100 flex items-start space-x-5 hover:border-primary-200 transition-all group">
                        <div className="w-14 h-14 bg-primary-50 text-primary-600 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                            <Clock className="w-7 h-7" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-slate-900 mb-1" style={headingTextStyle}>{hs.feature1Title || '30-Min Fast Delivery'}</h3>
                            <p className="text-slate-500 text-sm leading-relaxed" style={bodyTextStyle}>{hs.feature1Desc || 'Piping hot food delivered fresh with real-time GPS tracking.'}</p>
                        </div>
                    </div>
                    <div className="bg-white p-8 rounded-3xl shadow-xl border border-slate-100 flex items-start space-x-5 hover:border-primary-200 transition-all group">
                        <div className="w-14 h-14 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                            <Shield className="w-7 h-7" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-slate-900 mb-1" style={headingTextStyle}>{hs.feature2Title || 'Hygiene Guaranteed'}</h3>
                            <p className="text-slate-500 text-sm leading-relaxed" style={bodyTextStyle}>{hs.feature2Desc || 'FSSAI certified kitchen adhering to 5-star safety standards.'}</p>
                        </div>
                    </div>
                    <div className="bg-white p-8 rounded-3xl shadow-xl border border-slate-100 flex items-start space-x-5 hover:border-primary-200 transition-all group">
                        <div className="w-14 h-14 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                            <Truck className="w-7 h-7" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-slate-900 mb-1" style={headingTextStyle}>{hs.feature3Title || 'Free Delivery Offers'}</h3>
                            <p className="text-slate-500 text-sm leading-relaxed" style={bodyTextStyle}>{hs.feature3Desc || 'Enjoy zero delivery fee on all orders above ₹499.'}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* 2. GALLERY SECTION */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
                <div className="text-center max-w-3xl mx-auto mb-12">
                    <span className="text-primary-600 text-xs font-black uppercase tracking-widest bg-primary-50 px-3 py-1 rounded-full border border-primary-100">{hs.galleryBadge || 'Visual Feast'}</span>
                    <h2 className="text-3xl sm:text-5xl font-black text-slate-900 mt-3 mb-4" style={headingTextStyle}>{hs.galleryTitle || 'Our Culinary Gallery'}</h2>
                    <p className="text-slate-600 text-base sm:text-lg" style={bodyTextStyle}>{hs.galleryDesc || 'Take a glimpse into our signature dishes handcrafted daily by executive chefs using fresh organic ingredients.'}</p>
                    
                    {/* Category Filters */}
                    <div className="flex flex-wrap justify-center gap-2 mt-8">
                        {[
                            { id: 'all', label: 'All Showcase' },
                            ...(hs.galleryCategories || [
                                { id: 'ambiance', label: 'Restaurant Ambiance', icon: '🏛️' },
                                { id: 'mains', label: 'Signature Mains', icon: '🍕' },
                                { id: 'starters', label: 'Starters & Sides', icon: '🍟' },
                                { id: 'desserts', label: 'Desserts', icon: '🍰' },
                                { id: 'beverages', label: 'Mocktails & Drinks', icon: '🍹' }
                            ]).map(c => ({ id: c.id, label: `${c.icon || ''} ${c.label}`.trim() }))
                        ].map((cat) => (
                            <button
                                key={cat.id}
                                onClick={() => setGalleryCategory(cat.id)}
                                className={`px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${
                                    galleryCategory === cat.id
                                        ? 'bg-slate-900 text-white shadow-lg scale-105'
                                        : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                                }`}
                            >
                                {cat.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                    {filteredGallery.map((item) => (
                        <div key={item.id} className="group bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 border border-slate-100 flex flex-col">
                            <div className="relative h-64 overflow-hidden">
                                <img 
                                    src={item.image} 
                                    alt={item.title} 
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                />
                                <span className="absolute top-4 right-4 bg-slate-900/80 backdrop-blur-md text-amber-300 text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full border border-white/20">
                                    {item.tag}
                                </span>
                            </div>
                            <div className="p-6 flex justify-between items-center bg-white flex-1">
                                <div>
                                    <h3 className="font-bold text-lg text-slate-900 group-hover:text-primary-600 transition-colors" style={headingTextStyle}>{item.title}</h3>
                                    <p className="text-xs text-slate-400 capitalize mt-0.5" style={bodyTextStyle}>{item.category}</p>
                                </div>
                                <Link to="/menu" className="w-10 h-10 bg-slate-100 group-hover:bg-primary-600 group-hover:text-white rounded-full flex items-center justify-center transition-colors">
                                    <ArrowRight className="w-5 h-5" />
                                </Link>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* 3. LOCATION & OPERATING HOURS SECTION */}
            <section id="location-hours" className="bg-slate-900 text-white py-24 relative overflow-hidden" style={locationBgStyle}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        {/* Operating Hours */}
                        <div className="space-y-8">
                            <div>
                                <span className="text-amber-400 text-xs font-black uppercase tracking-widest bg-amber-400/10 px-3 py-1 rounded-full border border-amber-400/20">Planning A Visit?</span>
                                <h2 className="text-3xl sm:text-5xl font-black mt-3 mb-4">Operating Hours</h2>
                                <p className="text-slate-400 text-base">We are open 7 days a week serving fresh meals for dine-in, takeaway, and online delivery.</p>
                            </div>

                            <div className="space-y-4">
                                {operatingHours.map((oh, idx) => (
                                    <div key={idx} className="bg-slate-800/80 p-6 rounded-2xl border border-slate-700/60 flex justify-between items-center">
                                        <div className="flex items-center space-x-4">
                                            <div className="w-10 h-10 bg-primary-500/20 text-primary-400 rounded-xl flex items-center justify-center">
                                                <Clock className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-slate-200">{oh.day}</h4>
                                                <p className="text-xs text-slate-400 mt-0.5">{oh.time}</p>
                                            </div>
                                        </div>
                                        <span className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-xs font-bold px-3 py-1 rounded-full">
                                            {oh.status}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            <div className="bg-gradient-to-r from-primary-600 to-indigo-600 p-6 rounded-2xl flex items-center justify-between shadow-xl">
                                <div>
                                    <h4 className="font-black text-lg text-white">Craving Great Food Right Now?</h4>
                                    <p className="text-xs text-primary-100 mt-0.5">Order online for average 25-minute delivery</p>
                                </div>
                                <Link to="/menu" className="bg-white text-slate-900 font-black px-5 py-2.5 rounded-xl text-xs uppercase tracking-wider hover:bg-amber-300 transition-colors flex-shrink-0">
                                    Order Online
                                </Link>
                            </div>
                        </div>

                        {/* Interactive Map & Location */}
                        <div className="space-y-6">
                            <div className="bg-slate-800 p-6 rounded-3xl border border-slate-700 space-y-6">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-start space-x-4">
                                        <div className="w-12 h-12 bg-amber-400/20 text-amber-400 rounded-2xl flex items-center justify-center flex-shrink-0">
                                            <MapPin className="w-6 h-6" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-xl text-white">Find Our Restaurant</h3>
                                            <p className="text-slate-400 text-sm mt-1 leading-relaxed">{restaurantAddress}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Map Frame Mockup */}
                                <div className="relative h-72 w-full rounded-2xl overflow-hidden border border-slate-700 shadow-inner group">
                                    <iframe
                                        title="Restaurant Location Map"
                                        src={mapEmbedUrl}
                                        className="w-full h-full border-0 grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500"
                                        allowFullScreen=""
                                        loading="lazy"
                                    />
                                    <div className="absolute bottom-4 left-4 right-4 bg-slate-900/90 backdrop-blur-md p-3 rounded-xl border border-slate-700 flex justify-between items-center text-xs">
                                        <span className="font-bold text-slate-300">📌 Central City Branch</span>
                                        <a 
                                            href={`https://maps.google.com/?q=${encodeURIComponent(restaurantAddress)}`} 
                                            target="_blank" 
                                            rel="noreferrer"
                                            className="text-amber-400 font-bold hover:underline flex items-center gap-1"
                                        >
                                            Get Directions <ExternalLink className="w-3 h-3" />
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 4. CONTACT US & SOCIAL MEDIA SECTION */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
                <div className="grid lg:grid-cols-12 gap-12">
                    {/* Contact Info & Social Links */}
                    <div className="lg:col-span-5 space-y-8">
                        <div>
                            <span className="text-primary-600 text-xs font-black uppercase tracking-widest bg-primary-50 px-3 py-1 rounded-full border border-primary-100">Get In Touch</span>
                            <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mt-3 mb-4">Contact & Connect</h2>
                            <p className="text-slate-600 text-sm sm:text-base leading-relaxed">Have questions about party orders, dietary options, or reservations? Reach out to our hospitality team anytime.</p>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center space-x-4 bg-white p-5 rounded-2xl border border-slate-100 shadow-sm">
                                <div className="w-12 h-12 bg-primary-50 text-primary-600 rounded-xl flex items-center justify-center flex-shrink-0">
                                    <Phone className="w-6 h-6" />
                                </div>
                                <div>
                                    <p className="text-xs text-slate-400 font-bold uppercase">Phone / WhatsApp</p>
                                    <a href={`tel:${restaurantPhone}`} className="font-bold text-slate-900 hover:text-primary-600 transition-colors">{restaurantPhone}</a>
                                </div>
                            </div>

                            <div className="flex items-center space-x-4 bg-white p-5 rounded-2xl border border-slate-100 shadow-sm">
                                <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
                                    <Mail className="w-6 h-6" />
                                </div>
                                <div>
                                    <p className="text-xs text-slate-400 font-bold uppercase">Email Enquiries</p>
                                    <a href={`mailto:${restaurantEmail}`} className="font-bold text-slate-900 hover:text-indigo-600 transition-colors">{restaurantEmail}</a>
                                </div>
                            </div>
                        </div>

                        {/* SOCIAL MEDIA CHANNELS */}
                        {hs.displaySocialIcons !== false && (
                            <div className="pt-6 border-t border-slate-200">
                                <h4 className="text-sm font-black text-slate-900 uppercase tracking-wider mb-4">Follow Us On Social Media</h4>
                                <div className="grid grid-cols-2 gap-3">
                                    {hs.showInstagram !== false && hs.instagramUrl && (
                                        <a href={hs.instagramUrl} target="_blank" rel="noreferrer" className="flex items-center space-x-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-3.5 rounded-2xl font-bold text-xs hover:opacity-90 transition-opacity shadow-md">
                                            <Instagram className="w-5 h-5" />
                                            <span>Instagram</span>
                                        </a>
                                    )}
                                    {hs.showFacebook !== false && hs.facebookUrl && (
                                        <a href={hs.facebookUrl} target="_blank" rel="noreferrer" className="flex items-center space-x-3 bg-blue-600 text-white p-3.5 rounded-2xl font-bold text-xs hover:opacity-90 transition-opacity shadow-md">
                                            <Facebook className="w-5 h-5" />
                                            <span>Facebook</span>
                                        </a>
                                    )}
                                    {hs.showTwitter !== false && hs.twitterUrl && (
                                        <a href={hs.twitterUrl} target="_blank" rel="noreferrer" className="flex items-center space-x-3 bg-slate-900 text-white p-3.5 rounded-2xl font-bold text-xs hover:opacity-90 transition-opacity shadow-md">
                                            <Twitter className="w-5 h-5" />
                                            <span>X / Twitter</span>
                                        </a>
                                    )}
                                    {hs.showYoutube !== false && hs.youtubeUrl && (
                                        <a href={hs.youtubeUrl} target="_blank" rel="noreferrer" className="flex items-center space-x-3 bg-red-600 text-white p-3.5 rounded-2xl font-bold text-xs hover:opacity-90 transition-opacity shadow-md">
                                            <Youtube className="w-5 h-5" />
                                            <span>YouTube</span>
                                        </a>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Contact Form */}
                    <div className="lg:col-span-7">
                        <div className="bg-white p-8 sm:p-10 rounded-3xl shadow-xl border border-slate-100 space-y-6">
                            <h3 className="text-2xl font-black text-slate-900">Send Us a Direct Message</h3>
                            <form onSubmit={handleContactSubmit} className="space-y-4">
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Your Name</label>
                                        <input 
                                            type="text" 
                                            required
                                            placeholder="John Doe" 
                                            value={contactForm.name}
                                            onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                                            className="w-full bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm font-medium focus:ring-2 focus:ring-primary-500 outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Email Address</label>
                                        <input 
                                            type="email" 
                                            required
                                            placeholder="john@example.com" 
                                            value={contactForm.email}
                                            onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                                            className="w-full bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm font-medium focus:ring-2 focus:ring-primary-500 outline-none"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Phone Number</label>
                                    <input 
                                        type="tel" 
                                        placeholder="+91 99999 00000" 
                                        value={contactForm.phone}
                                        onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                                        className="w-full bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm font-medium focus:ring-2 focus:ring-primary-500 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Your Message / Inquiry</label>
                                    <textarea 
                                        rows="4" 
                                        required
                                        placeholder="Tell us how we can help you..." 
                                        value={contactForm.message}
                                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                                        className="w-full bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm font-medium focus:ring-2 focus:ring-primary-500 outline-none"
                                    ></textarea>
                                </div>
                                <button 
                                    type="submit" 
                                    disabled={submitting}
                                    className="w-full btn-primary py-4 text-sm font-black uppercase tracking-widest flex items-center justify-center space-x-2 shadow-xl shadow-primary-200"
                                >
                                    <Send className="w-4 h-4" />
                                    <span>{submitting ? 'Sending Message...' : 'Send Message'}</span>
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            {/* Table Booking Modal */}
            {showBookingModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
                    <TableBookingWidget 
                        restaurantId={tenant?.id} 
                        onClose={() => setShowBookingModal(false)} 
                    />
                </div>
            )}
        </div>
    );
}